import classNames from "classnames";
import { forwardRef, useId, useRef } from "react";
import { InputAttributes, NumberFormatValues, NumericFormat, NumericFormatProps } from "react-number-format";
import { useI18n } from "../../hooks/useTranslations";
import inputStyles from "./Input.module.css";

export type Props = Omit<NumericFormatProps<InputAttributes>, "onChange" | "onValueChange" | "onBlur"> & {
  isInvalid?: boolean;
  min?: number;
  width?: `${number}px`;
  hideThousandSeparator?: boolean;
  onBlur?: (value: number | null) => void;
  // onValueChange?: (value?: number) => void;
};

// react-number-format needs the literal separator characters (it does its own masking, it doesn't
// delegate to Intl), so pull them out of a locale-formatted sample via formatToParts instead of
// hardcoding them for one locale.
const getLocaleSeparators = (locale: string): { group: string; decimal: string } => {
  const parts = new Intl.NumberFormat(locale).formatToParts(1234567.8);
  return {
    group: parts.find((part) => part.type === "group")?.value ?? ",",
    decimal: parts.find((part) => part.type === "decimal")?.value ?? ".",
  };
};

const Numeric = forwardRef<HTMLInputElement, Props>(({ isInvalid, min, width, hideThousandSeparator, ...rest }, ref) => {
  const { language } = useI18n();
  const { autoComplete = "off", spellCheck = "false", id = useId() } = rest;
  const { group, decimal } = getLocaleSeparators(language);

  /* 
    "lastChange" is saved at each "onValueChange" and triggered only at "onBlur",
    because when we have the scenario "0,something" and "something" is deleted, 
    the "," is deleted as well.

    - at each "onValueChange" the info are saved
    - once "onBlur" is triggered, the last info is returned
    
  */

  const lastChange = useRef<NumberFormatValues | undefined>(undefined);

  return (
    <div className={inputStyles.container}>
      <NumericFormat
        {...rest}
        id={id}
        value={rest.value === undefined || rest.value === null ? "" : rest.value} // needed in order to reset to empty
        disabled={rest.disabled}
        getInputRef={ref}
        thousandSeparator={hideThousandSeparator ? false : group}
        decimalSeparator={decimal}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        onValueChange={(value) => {
          lastChange.current = value;
          // rest.onValueChange?.(lastChange.current.floatValue);
        }}
        onBlur={() => rest.onBlur?.(lastChange.current?.floatValue ?? null)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            rest.onBlur?.(lastChange.current?.floatValue ?? null);
          }
        }}
        onChange={() => {
          // needed in order to return a number instead a string
        }}
        style={{ width: width }}
        className={classNames(inputStyles.input, {
          [inputStyles.invalid]: isInvalid,
        })}
      />
    </div>
  );
});

export default Numeric;
